/**
 * deriveBadge — the client-side provenance honesty engine (DISP-02). PURE + data-driven.
 *
 * The "deferred" vs "mock" distinction is NOT a wire field: DataStatus is only
 * real | partial | mock | seed. The server's deferredProvenance() returns
 * dataStatus:'mock' with rowCount:0 + a gapReason. So the badge tier is derived from
 * dataStatus + rowCount TOGETHER:
 *   - real  + rows > 0        -> REAL      (green TEXT chip "REAL"; H5: status by color + LABEL,
 *                                           never a bare dot. H4: "REAL*" + caveat when populationCaveat)
 *   - real  + rowCount 0      -> REAL-empty "None on record" (M2: a genuine empty / 42 CFR 460.106
 *                                           compliance signal, NOT a feed gap — never DEFERRED)
 *   - partial                 -> PARTIAL   (amber chip, optional caveat tooltip)
 *   - rowCount === 0 (!real)  -> DEFERRED  (grey chip, "empty, NOT zero" — strictly deferredProvenance)
 *   - mock | seed + rows > 0  -> MOCK      (amber chip, synthetic data present)
 *
 * The branch order REAL(rows) -> REAL-empty -> PARTIAL -> DEFERRED(rowCount 0) -> MOCK is load-bearing.
 * Never hand-tag provenance: every tier flows from the server's meta.provenance through here.
 */
import type { Provenance } from "@shared/contracts";

export type Tier = "REAL" | "PARTIAL" | "MOCK" | "DEFERRED";

/** SkyBadge color subset used by the provenance engine (status by color + label, never amber-brand). */
export type BadgeColor = "success" | "warning" | "subtle";

export interface BadgeView {
  tier: Tier;
  /**
   * P1-7: a SHORT, CONSTANT chip label so it can never truncate in a narrow tile header:
   * "REAL" | "REAL*" | "None on record" | "PARTIAL" | "MOCK" | "DEFERRED". The feed/gap token is NOT
   * concatenated here (it used to be "MOCK · adt" / "DEFERRED · feed_not_landed") — it now lives in
   * `note` + `tooltip` so a long feed name clips nothing.
   */
  label: string;
  color: BadgeColor;
  /** REAL renders as a colored dot; every other tier renders a labelled text chip. */
  dot: boolean;
  /** Hover explainer (unblocking feed / gap reason). null only for REAL. */
  tooltip: string | null;
  /**
   * P1-7: the feed/gap token a DEFERRED/MOCK chip used to inline into its label (e.g. "feed_not_landed",
   * "adt"). Surfaced separately so a consumer with room (Dashboard tile, Data Health) can render it as an
   * inline caption that never clips. null for REAL/PARTIAL and when no feed/gap is known.
   */
  note: string | null;
}

export function deriveBadge(p: Provenance | undefined): BadgeView {
  // No provenance reported at all -> safest honest default is DEFERRED, never a silent pass.
  if (!p) {
    return { tier: "DEFERRED", label: "DEFERRED", color: "subtle", dot: false, tooltip: "No provenance reported", note: null };
  }

  const feed = p.provenanceFeed && p.provenanceFeed !== "none" ? p.provenanceFeed : null;
  const unblock = p.unblockingFeed ?? p.gapReason ?? null;

  // REAL: every row real.
  if (p.dataStatus === "real") {
    // M2: a genuinely-complete-but-empty REAL surface (rowCount 0) is "None on record" — a real,
    // meaningful signal (e.g. 42 CFR 460.106: a truly-absent care plan is a compliance fact, NOT a
    // feed gap). It must NOT fall through to DEFERRED "Awaiting feed" (reserved for deferredProvenance).
    if (p.rowCount === 0) {
      return {
        tier: "REAL",
        label: "None on record",
        color: "success",
        dot: false,
        tooltip: "Complete: none on record (a real empty, not a feed gap)",
        note: null,
      };
    }
    // H4: when the denominator/population is unproven (non-PACE sandbox), NEVER emit a bare clean REAL —
    // emit a caveated REAL* variant with a mandatory tooltip + a visible distinguishing marker.
    if (p.populationCaveat) {
      return {
        tier: "REAL",
        label: "REAL*",
        color: "success",
        dot: false,
        tooltip: "Denominator unproven on this non-PACE sandbox",
        note: null,
      };
    }
    // Gold/silver: a REAL op_* row whose human-readable label was resolved via a warehouse layer. Still
    // REAL (green), but the tooltip is transparent about WHICH layer supplied the name (honesty).
    if (p.provenanceFeed === "gold" || p.provenanceFeed === "silver") {
      return {
        tier: "REAL",
        label: "REAL",
        color: "success",
        dot: false,
        tooltip: `Resolved via ${p.provenanceFeed} warehouse layer`,
        note: null,
      };
    }
    // H5: REAL carries a VISIBLE "REAL" text label (tint success chip), never a bare color-only dot.
    return { tier: "REAL", label: "REAL", color: "success", dot: false, tooltip: null, note: null };
  }

  // PARTIAL: roster-pending / non-PACE-validated denominator (census, RES).
  if (p.dataStatus === "partial") {
    return {
      tier: "PARTIAL",
      label: "PARTIAL",
      color: "warning",
      dot: false,
      tooltip: p.populationCaveat
        ? "Denominator unproven on this sandbox (non-PACE-elderly)"
        : (unblock ?? "Partial signal"),
      note: null,
    };
  }

  // DEFERRED: feed-gated AND no rows yet -> "empty, not zero" (the dangerous-zero guard). P1-7: the chip
  // label stays the constant "DEFERRED"; the unblocking feed goes to `note` + the tooltip so it can never
  // truncate in a narrow tile header.
  if (p.rowCount === 0) {
    return {
      tier: "DEFERRED",
      label: "DEFERRED",
      color: "subtle",
      dot: false,
      tooltip: unblock ? `Awaiting: ${unblock}` : "No backing data yet (not a zero)",
      note: unblock,
    };
  }

  // MOCK: synthetic rows present but labeled mock/seed (e.g. utilization 451, hcc 31). P1-7: constant
  // "MOCK" chip label; the feed goes to `note` + the tooltip (never concatenated into the clipping label).
  return {
    tier: "MOCK",
    label: "MOCK",
    color: "warning",
    dot: false,
    tooltip: unblock ? `Mock until: ${unblock}` : "Synthetic data, feed pending",
    note: feed,
  };
}
